/**
 * Iframe-side postMessage bridge.
 *
 * The inverse of `src/embed/embed.ts` — this runs inside the chat iframe and
 * talks to the parent window (if one exists). Safe to call from any render
 * environment; no-ops when not embedded.
 */

import {
  type HostToIframe,
  type IframeToHost,
  isHostToIframe,
  PROTOCOL_VERSION,
} from "@/lib/embed-protocol";

/**
 * Distributive Omit — preserves discriminated union members when stripping
 * a shared field. Plain `Omit<Union, K>` collapses payload-specific fields.
 */
type DistributiveOmit<T, K extends keyof T> = T extends unknown
  ? Omit<T, K>
  : never;

export type IframeToHostPayload = DistributiveOmit<IframeToHost, "v">;

/** Are we running inside a (cross-origin) iframe? */
export function isEmbedded(): boolean {
  if (typeof window === "undefined") return false;
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin security exception reading window.top → definitely embedded.
    return true;
  }
}

/**
 * The origin of the page embedding us, as reported by `document.referrer` on
 * the initial navigation. This is not authenticated — the browser fills it
 * in, but a parent could navigate the iframe to forge it. Good enough for
 * deciding which `targetOrigin` to postMessage back to.
 */
export function getParentOrigin(): string | null {
  if (!isEmbedded()) return null;
  try {
    const ref = document.referrer;
    if (!ref) return null;
    return new URL(ref).origin;
  } catch {
    return null;
  }
}

/** Post a typed message to the parent. No-op if not embedded. */
export function postToHost(msg: IframeToHostPayload) {
  if (!isEmbedded()) return;
  const parent = getParentOrigin();
  if (!parent) return;
  const payload = { v: PROTOCOL_VERSION, ...msg } as IframeToHost;
  window.parent.postMessage(payload, parent);
}

/**
 * Subscribe to messages from the host page. Returns an unsubscribe fn.
 * Enforces protocol version + origin match.
 */
export function subscribeToHost(
  onMessage: (msg: HostToIframe) => void,
): () => void {
  if (!isEmbedded()) return () => {};
  const expectedOrigin = getParentOrigin();

  const handler = (event: MessageEvent) => {
    if (expectedOrigin && event.origin !== expectedOrigin) return;
    if (!isHostToIframe(event.data)) return;
    onMessage(event.data);
  };

  window.addEventListener("message", handler);
  return () => window.removeEventListener("message", handler);
}
